import { useState, useEffect, useCallback } from "react";
import {
  Clock,
  Calculator,
  Loader2,
  CheckSquare,
  Square,
  AlertCircle,
  Phone,
  ChevronDown,
  X,
  Search,
  Database,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { format } from "date-fns";
import type {
  AircallQueue,
  QueueMinutesResult,
} from "../types/interfaces";

interface QueueSelectionState {
  [queueId: string]: boolean;
}

export function MinutesUsage() {
  // Date range state
  const [startDate, setStartDate] = useState(
    format(new Date(new Date().setDate(1)), "yyyy-MM-dd")
  );
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));

  // Queue selection state
  const [queues, setQueues] = useState<AircallQueue[]>([]);
  const [queuesLoading, setQueuesLoading] = useState(false);
  const [selectedQueues, setSelectedQueues] = useState<QueueSelectionState>({});
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Calculation state
  const [calculating, setCalculating] = useState(false);
  const [calculationProgress, setCalculationProgress] = useState<{
    percentage: number;
    estimatedSecondsRemaining: number | null;
    processedQueues: number;
    totalQueues: number;
    currentBatch: number;
    totalBatches: number;
    rateLimitHits: number;
  } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState<{
    percentage: number;
    estimatedSecondsRemaining: number | null;
    daysCompleted: number;
    totalDays: number;
  } | null>(null);
  const [results, setResults] = useState<QueueMinutesResult[]>([]);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [calculationMeta, setCalculationMeta] = useState<{
    totalRecordsScanned?: number;
    calculationTimeMs?: number;
    source?: string;
    requestsMade?: number;
    rateLimitHits?: number;
  } | null>(null);

  // Cache key for localStorage
  const QUEUES_CACHE_KEY = "aircall_queues_cache";
  const QUEUES_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

  // Load queues from cache first, then fetch fresh data in background
  useEffect(() => {
    loadQueuesWithCache();
  }, []);

  const loadQueuesWithCache = async () => {
    // Try to load from localStorage first for instant display
    try {
      const cached = localStorage.getItem(QUEUES_CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const age = Date.now() - timestamp;
        if (age < QUEUES_CACHE_TTL && Array.isArray(data) && data.length > 0) {
          setQueues(data);
          setQueuesLoading(false);
          // Refresh in background if cache is older than 5 minutes
          if (age > 5 * 60 * 1000) {
            fetchQueuesInBackground();
          }
          return;
        }
      }
    } catch (e) {
      console.warn("Failed to load queues from cache:", e);
    }

    // No valid cache, fetch from API
    await fetchQueues();
  };

  const fetchQueuesInBackground = async () => {
    try {
      const response = await fetch("/api/v1/aircall/queues/all");
      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.data)) {
          setQueues(data.data);
          localStorage.setItem(QUEUES_CACHE_KEY, JSON.stringify({
            data: data.data,
            timestamp: Date.now(),
          }));
        }
      }
    } catch (e) {
      console.warn("Background queue refresh failed:", e);
    }
  };

  const fetchQueues = async () => {
    setQueuesLoading(true);
    try {
      const response = await fetch("/api/v1/aircall/queues/all");
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.success) {
        const queuesData = data.data || [];
        setQueues(queuesData);
        // Save to localStorage for next time
        localStorage.setItem(QUEUES_CACHE_KEY, JSON.stringify({
          data: queuesData,
          timestamp: Date.now(),
        }));
      } else {
        toast.error(data.error || "Failed to fetch queues");
      }
    } catch (error) {
      console.error("Failed to fetch queues:", error);
      toast.error("Failed to fetch queues from Aircall");
    } finally {
      setQueuesLoading(false);
    }
  };

  // Filter queues based on search
  const filteredQueues = queues.filter((queue) =>
    queue.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Selection helpers
  const selectedCount = Object.values(selectedQueues).filter(Boolean).length;

  const handleSelectAll = () => {
    const newSelection: QueueSelectionState = {};
    queues.forEach((queue) => {
      newSelection[queue.id] = true;
    });
    setSelectedQueues(newSelection);
  };

  const handleSelectNone = () => {
    setSelectedQueues({});
  };

  const handleToggleQueue = (queueId: string) => {
    setSelectedQueues((prev) => ({
      ...prev,
      [queueId]: !prev[queueId],
    }));
  };

  // Get selected queue objects
  const getSelectedQueueObjects = useCallback(() => {
    return queues.filter((queue) => selectedQueues[queue.id]);
  }, [queues, selectedQueues]);

  // Poll calculation job status
  const pollCalculationStatus = async (jobId: string): Promise<void> => {
    const pollInterval = 2000; // 2 seconds
    const maxPolls = 600; // 20 minutes max
    let polls = 0;

    while (polls < maxPolls) {
      try {
        const response = await fetch(`/api/v1/aircall/calculate-status/${jobId}`);
        const data = await response.json();

        if (!data.success) {
          throw new Error(data.error || "Failed to get calculation status");
        }

        const {
          status,
          percentage,
          estimatedSecondsRemaining,
          processedQueues,
          totalQueues,
          currentBatch,
          totalBatches,
          totalRecordsFetched,
          results: jobResults,
          summary,
          rateLimitInfo,
          elapsedSeconds,
          error,
        } = data.data;

        setCalculationProgress({
          percentage,
          estimatedSecondsRemaining,
          processedQueues,
          totalQueues,
          currentBatch,
          totalBatches,
          rateLimitHits: rateLimitInfo?.rateLimitHits || 0,
        });

        if (status === "completed") {
          const results = jobResults || [];
          setResults(results);
          setCalculationMeta({
            totalRecordsScanned: totalRecordsFetched,
            calculationTimeMs: elapsedSeconds * 1000,
            source: rateLimitInfo?.requestsMade > 1 ? "Aircall API (batch)" : "database (cached)",
            requestsMade: rateLimitInfo?.requestsMade,
            rateLimitHits: rateLimitInfo?.rateLimitHits,
          });
          setHasCalculated(true);
          
          if (results.length === 0) {
            toast.error("No records found for the selected date range and queues");
          } else {
            toast.success(
              `Calculated ${summary?.totalMinutes?.toFixed(2) || 0} minutes for ${totalQueues} queue(s)`
            );
          }
          
          setCalculating(false);
          setCalculationProgress(null);
          return;
        }

        if (status === "failed") {
          throw new Error(error || "Calculation failed");
        }

        // Wait before next poll
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
        polls++;
      } catch (error) {
        console.error("Polling error:", error);
        toast.error(error instanceof Error ? error.message : "Calculation failed");
        setCalculating(false);
        setCalculationProgress(null);
        return;
      }
    }

    toast.error("Calculation timed out after 20 minutes");
    setCalculating(false);
    setCalculationProgress(null);
  };

  // Calculate minutes usage - uses batch processing with rate limiting
  const handleCalculate = async () => {
    const selected = getSelectedQueueObjects();
    if (selected.length === 0) {
      toast.error("Please select at least one queue");
      return;
    }

    // Allow same-day selection (e.g., 1/26 to 1/26)
    if (new Date(startDate) > new Date(endDate)) {
      toast.error("Start date must be before or equal to end date");
      return;
    }

    setCalculating(true);
    setResults([]);
    setHasCalculated(false);
    setCalculationMeta(null);
    setCalculationProgress({
      percentage: 0,
      estimatedSecondsRemaining: null,
      processedQueues: 0,
      totalQueues: selected.length,
      currentBatch: 0,
      totalBatches: Math.ceil(selected.length / 10),
      rateLimitHits: 0,
    });

    try {
      const queueIds = selected.map((q) => q.id);
      const queueNames: Record<string, string> = {};
      selected.forEach((q) => {
        queueNames[q.id] = q.name;
      });

      // Try fast DB-based calculation first (instant if data is cached)
      const fastResponse = await fetch("/api/v1/aircall/minutes-usage-fast", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ startDate, endDate, queueIds, queueNames }),
      });

      if (fastResponse.ok) {
        const fastData = await fastResponse.json();
        if (fastData.success && !fastData.data.meta?.noData) {
          // Fast calculation succeeded with cached data
          const results = fastData.data.results || [];
          if (results.length === 0) {
            toast.error("No records found for the selected date range and queues");
            setCalculating(false);
            setCalculationProgress(null);
            setHasCalculated(true);
            setResults([]);
            return;
          }
          setResults(results);
          setCalculationMeta({
            ...fastData.data.meta,
            source: "database (instant)",
          });
          setHasCalculated(true);
          toast.success(
            `Calculated minutes for ${results.length} queue(s) instantly from cache`
          );
          setCalculating(false);
          setCalculationProgress(null);
          return;
        } else if (fastData.success && fastData.data.meta?.noData) {
          // No data found
          toast.error("No records found for the selected date range and queues");
          setCalculating(false);
          setCalculationProgress(null);
          setHasCalculated(true);
          setResults([]);
          return;
        }
      }

      // Fallback to batch calculation with rate limiting
      toast.loading("Starting batch calculation with rate limiting...", { id: "calc" });

      const response = await fetch("/api/v1/aircall/calculate-batch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          startDate,
          endDate,
          queueIds,
          queueNames,
          useCache: true, // Try cache first within the batch job
        }),
      });

      toast.dismiss("calc");

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.data.jobId) {
        // Start polling for progress
        await pollCalculationStatus(data.data.jobId);
      } else {
        throw new Error(data.error || "Failed to start calculation");
      }
    } catch (error) {
      toast.dismiss("calc");
      console.error("Failed to calculate minutes usage:", error);
      toast.error(error instanceof Error ? error.message : "Failed to calculate minutes usage");
      setCalculating(false);
      setCalculationProgress(null);
    }
  };

  // Format seconds to human-readable time
  const formatTimeRemaining = (seconds: number): string => {
    if (seconds < 60) {
      return `${seconds}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSecs = seconds % 60;
    if (minutes < 60) {
      return remainingSecs > 0 ? `${minutes}m ${remainingSecs}s` : `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMins = minutes % 60;
    return `${hours}h ${remainingMins}m`;
  };

  // Poll sync job status
  const pollSyncStatus = async (jobId: string): Promise<void> => {
    const pollInterval = 2000; // 2 seconds
    const maxPolls = 600; // 20 minutes max
    let polls = 0;

    while (polls < maxPolls) {
      try {
        const response = await fetch(`/api/v1/aircall/sync-status/${jobId}`);
        const data = await response.json();

        if (!data.success) {
          throw new Error(data.error || "Failed to get sync status");
        }

        const { status, percentage, estimatedSecondsRemaining, daysCompleted, totalDays, recordsFetched, totalRecords, recordsInserted, queueCount, error } = data.data;

        // Use new fields (days) if available, fallback to old fields (records) for backward compatibility
        setSyncProgress({
          percentage,
          estimatedSecondsRemaining,
          daysCompleted: daysCompleted ?? recordsFetched ?? 0,
          totalDays: totalDays ?? totalRecords ?? 0,
        });

        if (status === "completed") {
          toast.success(`Synced ${recordsInserted} call records from ${queueCount} queues`);
          setSyncing(false);
          setSyncProgress(null);
          return;
        }

        if (status === "failed") {
          throw new Error(error || "Sync failed");
        }

        // Wait before next poll
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
        polls++;
      } catch (error) {
        console.error("Polling error:", error);
        toast.error(error instanceof Error ? error.message : "Sync failed");
        setSyncing(false);
        setSyncProgress(null);
        return;
      }
    }

    toast.error("Sync timed out after 20 minutes");
    setSyncing(false);
    setSyncProgress(null);
  };

  // Sync call data to database for faster future calculations
  // Syncs ALL calls for the date range - queue filtering happens during calculation
  const handleSyncData = async () => {
    // Allow same-day selection (e.g., 1/26 to 1/26)
    if (new Date(startDate) > new Date(endDate)) {
      toast.error("Start date must be before or equal to end date");
      return;
    }

    setSyncing(true);
    setSyncProgress({ percentage: 0, estimatedSecondsRemaining: null, daysCompleted: 0, totalDays: 0 });

    try {
      // Note: We sync ALL calls, not filtered by queue
      // Queue filtering is done locally during calculation for better performance
      const response = await fetch("/api/v1/aircall/sync-calls", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ startDate, endDate }), // No queueIds - fetch all
      });

      const data = await response.json();

      if (data.success && data.data.jobId) {
        // Start polling for progress
        await pollSyncStatus(data.data.jobId);
      } else {
        toast.error(data.error || "Failed to start sync");
        setSyncing(false);
        setSyncProgress(null);
      }
    } catch (error) {
      console.error("Failed to sync data:", error);
      toast.error("Failed to sync data from Aircall");
      setSyncing(false);
      setSyncProgress(null);
    }
  };

  // Calculate totals from results (excluding test calls - they're already excluded by API)
  const totalMinutes = results.reduce((sum, r) => sum + (r.totalMinutes || 0), 0);
  const totalCalls = results.reduce((sum, r) => sum + (r.totalCalls || 0), 0);
  const totalAnsweredCalls = results.reduce(
    (sum, r) => sum + (r.totalAnsweredCalls || 0),
    0
  );
  // Test call totals
  const totalTestCalls = results.reduce((sum, r) => sum + (r.testCalls || 0), 0);
  const totalTestMinutes = results.reduce((sum, r) => sum + (r.testMinutes || 0), 0);
  // Total duration (all calls including test calls)
  const totalDurationMinutes = results.reduce((sum, r) => sum + (r.totalDurationMinutes || 0), 0);

  // Format minutes to display
  const formatMinutes = (minutes: number) => {
    if (minutes < 1) {
      return minutes.toFixed(3);
    }
    return minutes.toFixed(2);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Minutes Usage</h1>
          <p className="text-sm text-gray-500 mt-1">
            Calculate billable minutes for selected queues over a date range
          </p>
        </div>
        <Clock className="h-8 w-8 text-blue-500" />
      </div>

      {/* Configuration Card */}
      <div className="bg-white rounded-lg shadow border p-6 space-y-6">
        {/* Date Range Section */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Date Range</h3>
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <label
                htmlFor="startDate"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Start Date
              </label>
              <input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex-1">
              <label
                htmlFor="endDate"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                End Date
              </label>
              <input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Queue Selection Section */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Queue Selection
          </h3>

          {queuesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 text-blue-500 animate-spin mr-2" />
              <span className="text-gray-500">Loading queues...</span>
            </div>
          ) : queues.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">No queues available</p>
              <button
                onClick={fetchQueues}
                className="mt-2 text-blue-600 hover:text-blue-700 text-sm"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Selection summary and dropdown trigger */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <div className="flex items-center gap-2">
                    <Phone className="h-5 w-5 text-gray-400" />
                    <span className="text-gray-700">
                      {selectedCount === 0
                        ? "Select queues..."
                        : selectedCount === queues.length
                        ? `All ${queues.length} queues selected`
                        : `${selectedCount} of ${queues.length} queues selected`}
                    </span>
                  </div>
                  <ChevronDown
                    className={`h-5 w-5 text-gray-400 transition-transform ${
                      dropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {/* Dropdown */}
                {dropdownOpen && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden">
                    {/* Search and bulk actions */}
                    <div className="p-3 border-b border-gray-200 space-y-2">
                      {/* Search input */}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search queues..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {searchQuery && (
                          <button
                            onClick={() => setSearchQuery("")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>

                      {/* Bulk actions */}
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleSelectAll}
                          className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={handleSelectNone}
                          className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
                        >
                          Select None
                        </button>
                        <span className="text-xs text-gray-500 ml-auto">
                          {filteredQueues.length} queues
                        </span>
                      </div>
                    </div>

                    {/* Queue list */}
                    <div className="max-h-56 overflow-y-auto">
                      {filteredQueues.length === 0 ? (
                        <div className="py-4 text-center text-sm text-gray-500">
                          No queues match your search
                        </div>
                      ) : (
                        filteredQueues.map((queue) => (
                          <button
                            key={queue.id}
                            type="button"
                            onClick={() => handleToggleQueue(queue.id)}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left"
                          >
                            {selectedQueues[queue.id] ? (
                              <CheckSquare className="h-5 w-5 text-blue-600 flex-shrink-0" />
                            ) : (
                              <Square className="h-5 w-5 text-gray-300 flex-shrink-0" />
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {queue.name}
                              </p>
                              {queue.description && (
                                <p className="text-xs text-gray-500 truncate">
                                  {queue.description}
                                </p>
                              )}
                            </div>
                          </button>
                        ))
                      )}
                    </div>

                    {/* Footer with close */}
                    <div className="p-2 border-t border-gray-200 bg-gray-50">
                      <button
                        type="button"
                        onClick={() => setDropdownOpen(false)}
                        className="w-full py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Selected queues tags */}
              {selectedCount > 0 && selectedCount <= 10 && (
                <div className="flex flex-wrap gap-2">
                  {getSelectedQueueObjects().map((queue) => (
                    <span
                      key={queue.id}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
                    >
                      {queue.name}
                      <button
                        type="button"
                        onClick={() => handleToggleQueue(queue.id)}
                        className="hover:text-blue-900"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Calculate and Sync Buttons */}
        <div className="pt-4 border-t">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleCalculate}
              disabled={calculating || syncing || selectedCount === 0}
              className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {calculating ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Calculating...
                </>
              ) : (
                <>
                  <Calculator className="h-5 w-5" />
                  Calculate Minutes Usage
                </>
              )}
            </button>
            <button
              onClick={handleSyncData}
              disabled={calculating || syncing}
              className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 border border-gray-300"
              title="Sync call data from Aircall to local database for faster calculations"
            >
              {syncing ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <Database className="h-5 w-5" />
                  Sync Data to Cache
                </>
              )}
            </button>
          </div>

          {/* Calculation Progress Bar */}
          {calculating && calculationProgress && (
            <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-green-800">
                  Calculating... {calculationProgress.percentage}%
                  {calculationProgress.estimatedSecondsRemaining !== null && (
                    <span className="ml-2 text-green-600">
                      - ~{formatTimeRemaining(calculationProgress.estimatedSecondsRemaining)} remaining
                    </span>
                  )}
                </span>
                <span className="text-xs text-green-600">
                  Batch {calculationProgress.currentBatch} / {calculationProgress.totalBatches}
                </span>
              </div>
              <div className="w-full bg-green-200 rounded-full h-2.5 mb-2">
                <div
                  className="bg-green-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${calculationProgress.percentage}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-xs text-green-600">
                <span>
                  {calculationProgress.processedQueues} / {calculationProgress.totalQueues} queues processed
                </span>
                {calculationProgress.rateLimitHits > 0 && (
                  <span className="text-amber-600">
                    ⚠️ {calculationProgress.rateLimitHits} rate limit hit(s)
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Sync Progress Bar */}
          {syncing && syncProgress && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-800">
                  Syncing... {syncProgress.percentage}%
                  {syncProgress.estimatedSecondsRemaining !== null && (
                    <span className="ml-2 text-blue-600">
                      - ~{formatTimeRemaining(syncProgress.estimatedSecondsRemaining)} remaining
                    </span>
                  )}
                </span>
                <span className="text-xs text-blue-600">
                  {syncProgress.daysCompleted.toLocaleString()} / {syncProgress.totalDays.toLocaleString()} days
                </span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${syncProgress.percentage}%` }}
                />
              </div>
            </div>
          )}

          {selectedCount === 0 && !queuesLoading && queues.length > 0 && (
            <p className="mt-2 text-sm text-amber-600">
              Please select at least one queue to calculate
            </p>
          )}
          {!syncing && (
            <p className="mt-2 text-xs text-gray-500">
              Tip: Click "Sync Data to Cache" first to download call data locally, then calculations will be instant.
            </p>
          )}
        </div>
      </div>

      {/* Results Section */}
      {hasCalculated && (
        <div className="bg-white rounded-lg shadow border overflow-hidden">
          {/* Summary Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
            <h3 className="text-lg font-semibold text-white mb-3">
              Minutes Usage Results
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-xs text-blue-100 uppercase tracking-wide">
                  Total Calls
                </p>
                <p className="text-2xl font-bold text-white">
                  {totalCalls.toLocaleString()}
                </p>
                <p className="text-xs text-blue-200 mt-1">(Excludes test calls)</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-xs text-blue-100 uppercase tracking-wide">
                  Answered Calls
                </p>
                <p className="text-2xl font-bold text-white">
                  {totalAnsweredCalls.toLocaleString()}
                </p>
              </div>
              <div className="bg-white/10 rounded-lg p-3 border-l-2 border-yellow-300">
                <p className="text-xs text-blue-100 uppercase tracking-wide">
                  Call Duration
                </p>
                <p className="text-2xl font-bold text-white">
                  {formatMinutes(totalDurationMinutes)}
                </p>
                <p className="text-xs text-blue-200 mt-1">(All calls)</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3 border-l-2 border-green-300">
                <p className="text-xs text-blue-100 uppercase tracking-wide">
                  Billable Call Time
                </p>
                <p className="text-2xl font-bold text-white">
                  {formatMinutes(totalMinutes)}
                </p>
                <p className="text-xs text-blue-200 mt-1">(Excludes test calls)</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3 border-l-2 border-red-300">
                <p className="text-xs text-blue-100 uppercase tracking-wide">
                  Test Calls
                </p>
                <p className="text-2xl font-bold text-white">
                  {totalTestCalls.toLocaleString()}
                </p>
                <p className="text-xs text-blue-200 mt-1">
                  {formatMinutes(totalTestMinutes)} min
                </p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-xs text-blue-100 uppercase tracking-wide">
                  Date Range
                </p>
                <p className="text-sm font-medium text-white">
                  {format(new Date(startDate), "MMM d")} -{" "}
                  {format(new Date(endDate), "MMM d, yyyy")}
                </p>
              </div>
            </div>
            {/* Calculation metadata */}
            {calculationMeta && (
              <div className="mt-3 pt-3 border-t border-white/20 flex flex-wrap items-center gap-4 text-xs text-blue-100">
                {calculationMeta.totalRecordsScanned !== undefined && (
                  <span>
                    📊 {calculationMeta.totalRecordsScanned.toLocaleString()} records scanned
                  </span>
                )}
                {calculationMeta.calculationTimeMs !== undefined && (
                  <span>
                    ⏱️ {(calculationMeta.calculationTimeMs / 1000).toFixed(1)}s
                  </span>
                )}
                {calculationMeta.requestsMade !== undefined && calculationMeta.requestsMade > 1 && (
                  <span>
                    🔄 {calculationMeta.requestsMade} API requests
                  </span>
                )}
                {calculationMeta.rateLimitHits !== undefined && calculationMeta.rateLimitHits > 0 && (
                  <span className="text-amber-200">
                    ⚠️ {calculationMeta.rateLimitHits} rate limit hit(s)
                  </span>
                )}
                {calculationMeta.source && (
                  <span>
                    📍 Source: {calculationMeta.source}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Results Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Queue Name
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Calls
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Answered Calls
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Call Duration
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Billable Call Time
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-l-2 border-red-200">
                    Test Calls
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Test Minutes
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {results.map((result) => (
                  <tr key={result.queueId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {result.queueName}
                      </div>
                      <div className="text-xs text-gray-500 font-mono">
                        {result.queueId}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="text-sm text-gray-700">
                        {result.totalCalls.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="text-sm text-gray-700">
                        {result.totalAnsweredCalls.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="text-sm text-gray-600">
                        {formatMinutes(result.totalDurationMinutes || 0)}
                      </span>
                      <span className="text-xs text-gray-400 ml-1">(all calls)</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="text-sm font-semibold text-green-700">
                        {formatMinutes(result.totalMinutes)}
                      </span>
                      <span className="text-xs text-gray-400 ml-1">(billable)</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right border-l-2 border-red-100">
                      <span className="text-sm text-red-700">
                        {(result.testCalls || 0).toLocaleString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="text-sm text-red-700">
                        {formatMinutes(result.testMinutes || 0)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {result.error ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                          <AlertCircle className="h-3 w-3" />
                          Error
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                          <CheckSquare className="h-3 w-3" />
                          Success
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              {/* Footer totals */}
              <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-bold text-gray-900">
                      Total ({results.length} queues)
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span className="text-sm font-bold text-gray-900">
                      {totalCalls.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span className="text-sm font-bold text-gray-900">
                      {totalAnsweredCalls.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span className="text-sm font-bold text-gray-600">
                      {formatMinutes(totalDurationMinutes)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span className="text-sm font-bold text-green-700">
                      {formatMinutes(totalMinutes)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right border-l-2 border-red-200">
                    <span className="text-sm font-bold text-red-700">
                      {totalTestCalls.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span className="text-sm font-bold text-red-700">
                      {formatMinutes(totalTestMinutes)}
                    </span>
                  </td>
                  <td className="px-6 py-4"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Empty state when no calculation done yet */}
      {!hasCalculated && !calculating && (
        <div className="bg-white rounded-lg shadow border p-12 text-center">
          <Clock className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Results Yet
          </h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            Select a date range and one or more queues, then click "Calculate
            Minutes Usage" to see the results.
          </p>
        </div>
      )}
    </div>
  );
}
